// Shared types + human-friendly label helpers for the Google Address
// Validation verdict the BE persists at
// `lead.polygonData.addressVerdict` for CSV-imported leads. Operators
// shouldn't need to learn Google's PREMISE / ROUTE / OTHER vocabulary
// to read the right panel - these helpers map the raw enum values to
// plain English.

export interface AddressVerdict {
  validationGranularity: string | null;
  addressComplete: boolean | null;
  hasUnconfirmedComponents: boolean | null;
  hasInferredComponents: boolean | null;
  inferredComponents: Array<{
    type: string | null;
    value: string | null;
    inferred?: boolean;
    replaced?: boolean;
    spellCorrected?: boolean;
  }>;
  unconfirmedComponents: Array<{
    type: string | null;
    value: string | null;
    confirmationLevel: string | null;
  }>;
  formattedAddress: string | null;
}

const ADDRESS_GRANULARITY_LABEL: Record<string, string> = {
  PREMISE: "Located the building",
  SUB_PREMISE: "Located a unit within the building",
  ROUTE: "Street-level only (no specific building)",
  BLOCK: "Block-level only",
  PREMISE_PROXIMITY: "Located near the building",
  OTHER: "Couldn't recognise the address",
};

const COMPONENT_CONFIRMATION_LABEL: Record<string, string> = {
  CONFIRMED: "confirmed",
  UNCONFIRMED_BUT_PLAUSIBLE: "looks plausible but unconfirmed",
  UNCONFIRMED_AND_SUSPICIOUS: "couldn't verify, looks wrong",
};

const COMPONENT_TYPE_LABEL: Record<string, string> = {
  route: "street",
  postal_code: "postcode",
  locality: "town",
  postal_town: "postal town",
  administrative_area_level_1: "region",
  administrative_area_level_2: "county",
  country: "country",
  premise: "building",
  subpremise: "unit",
  street_number: "street number",
};

export const humanizeGranularity = (g: string | null | undefined): string => {
  if (!g) return "no verdict";
  return ADDRESS_GRANULARITY_LABEL[g.toUpperCase()] ?? g;
};

export const humanizeComponentType = (
  t: string | null | undefined
): string => {
  if (!t) return "?";
  return COMPONENT_TYPE_LABEL[t] ?? t.replace(/_/g, " ");
};

export const humanizeConfirmation = (
  c: string | null | undefined
): string => {
  if (!c) return "?";
  return (
    COMPONENT_CONFIRMATION_LABEL[c] ?? c.toLowerCase().replace(/_/g, " ")
  );
};
